import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { TablePagination } from '@mui/material';
import axios from 'axios';
import {
  Calendar,
  ChevronDown,
  FileDown,
  Filter,
  ArrowLeft,
  Search,
  Table,
} from 'lucide-react';
import NewResultsFilters from 'src/components/Filters/NewResultsFilters';
import { Button } from 'src/components/ui/button';
import {
  buildFlowNameLookup,
  getOrganizationMemberLabel,
  getOrganizationMembers,
  isSonioDemoOrganization,
  type OrganizationMember,
} from 'src/services/organizations.service';
import { getCmsApiBaseUrl } from 'src/services/api.config';
import TableViewModal, {
  Column,
} from 'src/content/pages/Sessions/TableViewModal';
import { SonioDemoFlowFilterContext } from 'src/contexts/SonioDemoFlowFilterContext';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { fetchTransactions } from 'src/store/slices/transaction.slice';

import ActiveFiltersRow from './ActiveFiltersRow';
import AssignModal from './AssignModal';
import ExportDateDialog from './ExportDateDialog';
import SessionsTableRow from './SessionsTableRow';
// NOTE: We intentionally avoid debouncing the initial table fetch because
// redux-persist rehydrates cached rows; a debounce would cause a "stale flash".

const TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_IDS = [
  '65e877d12156d096dbe51ca0',
  '686bca6cf9073ffc24b2ff8c',
  '65e877632156d096dbe51c9e',
  '65e877c02156d096dbe51c9f',
];

const TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_ID_SET = new Set(
  TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_IDS,
);

const TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_IDS = [
  '67d1537a0816c4f8be3981f2', // staging
  '69aea238c9aa438c178a58f6', // staging
  '68877911895b0db560b1268c', // live
  '69b12ad8a22ba550370c7152', // live
];

const TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_ID_SET = new Set(
  TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_IDS,
);

// Flow IDs that should not appear in Session table flow filters.
const TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_IDS = [
  '667bd06920b3b7d3d3a64660',
  '65ca169f8db0af6615cc61e7',
  '667bd07020b3b7d3d3a64661',
  '667bd07920b3b7d3d3a64662',
  '66a0ddf810e4b3d220f82de0',
  '67112cbb5507b606aa9cdbd5',
  '673c660560d53e97ce601d9d',
];

const TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET = new Set(
  TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_IDS,
);

const Results = () => {
  // Add CSS for white checkmark in checkboxes
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      input[type="checkbox"] {
        position: relative;
        appearance: none;
        -webkit-appearance: none;
        width: 16px;
        height: 16px;
        border: 2px solid #d1d5db;
        border-radius: 3px;
        cursor: pointer;
      }
      input[type="checkbox"]:checked {
        background-color: #00387D;
        border-color: #00387D;
      }
      input[type="checkbox"]:checked::after {
        content: '';
        display: block;
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
        position: absolute;
        top: 1px;
        left: 4px;
      }
      
      /* Radio button styling */
      input[type="radio"] {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        width: 16px;
        height: 16px;
        border: 2px solid #d1d5db;
        border-radius: 50%;
        background-color: white;
        cursor: pointer;
        position: relative;
        transition: all 0.2s;
      }
      
      input[type="radio"]:checked {
        background-color: #00387D;
        border-color: white;
        box-shadow: 0 0 0 2px #00387D;
      }
      
      input[type="radio"]:checked::after {
        content: '';
        display: block;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: white;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      
      input[type="radio"]:hover {
        border-color: #00387D;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [page, setPage] = useState(0);
  const [first, setFirst] = useState(20);

  const [filters, setFilters] = useState<any>({
    sonioId: '',
    referenceId: '',
    flowName: [],
    fullName: '',
    firstName: '',
    lastName: '',
    dateRange: '',
    createdStartDate: '',
    createdEndDate: '',
    sortOrder: '',
    status: [] as string[],
    result: [] as string[],
    assignedUserId: '',
    assignedUserName: '',
    assigneeLabel: '',
  });
  const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([]);
  const [orgMembersLoading, setOrgMembersLoading] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  // Temporary state for date range before applying
  const [tempDateRange, setTempDateRange] = useState({
    start: '',
    end: '',
  });

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<Record<string, HTMLElement | null>>({});
  const [savedFilters, setSavedFilters] = useState<
    { key: string; label: string; value: string | string[] }[]
  >([]);
  // Prevent an initial "unfiltered" fetch before URL query filters hydrate.
  // This avoids a cached/stale flash + double network calls when navigating back.
  const [filtersHydratedFromUrl, setFiltersHydratedFromUrl] = useState(false);
  const { getIdTokenClaims } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const fromStatistics =
    (location.state as { fromStatistics?: boolean } | null)?.fromStatistics ===
      true || new URLSearchParams(location.search).get('from') === 'statistics';
  const dispatch = useAppDispatch();
  const { selectedOrganization, organizations } = useAppSelector(
    state => state.organization,
  );
  const { sliceTransactions, totalCount, isLoading, liveData } = useAppSelector(
    state => state.sliceTransaction,
  );
  const sonioDemoFlowFilter = useContext(SonioDemoFlowFilterContext);
  const isSonioDemoOrg = isSonioDemoOrganization(selectedOrganization);

  const flowNameLookup = useMemo(
    () => buildFlowNameLookup(organizations),
    [organizations],
  );

  const isTipicoAffordabilitySportsGermany =
    selectedOrganization?.display_name?.trim().toLowerCase() ===
      'tipico affordability sports germany' ||
    selectedOrganization?.name?.trim().toLowerCase().replace(/_/g, ' ') ===
      'tipico sports';

  const isTipicoIdvSportsGermany =
    selectedOrganization?.display_name?.trim().toLowerCase() ===
      'tipico idv sports germany' ||
    selectedOrganization?.name?.trim().toLowerCase().replace(/_/g, ' ') ===
      'tipico idv sports germany';
  // Flow IDs to pass when Sonio Demo is selected. If the user hasn't ticked
  // anything yet, fall back to *their own* profile flows (from /profile.flowids)
  // instead of the full org list — otherwise restricted users would briefly
  // see every other tenant's sessions while the profile fetch resolves.
  const sonioDemoFlowIds = useMemo<string[] | undefined>(() => {
    if (!isSonioDemoOrg) return undefined;
    if (sonioDemoFlowFilter?.selectedFlowIds?.length) {
      return sonioDemoFlowFilter.selectedFlowIds;
    }
    if (sonioDemoFlowFilter?.profileFlowIds?.length) {
      return sonioDemoFlowFilter.profileFlowIds;
    }
    return selectedOrganization?.flowIds ?? [];
  }, [
    isSonioDemoOrg,
    sonioDemoFlowFilter?.selectedFlowIds,
    sonioDemoFlowFilter?.profileFlowIds,
    selectedOrganization?.flowIds,
  ]);

  // When URL or saved filters have flowIds, use those for the API so the table respects the filter
  const effectiveFlowIdsForRequest = useMemo(() => {
    const fromFilter = savedFilters.find(
      f => f.key === 'flowIds' || f.key === 'flowName',
    );

    const parseFlowIds = (raw: string | string[]) => {
      const parsed = Array.isArray(raw)
        ? raw
        : raw
          ? String(raw)
              .trim()
              .split(',')
              .map(s => s.trim())
          : [];

      if (isTipicoAffordabilitySportsGermany) {
        return parsed.filter(id =>
          TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_ID_SET.has(id),
        );
      }

      if (isTipicoIdvSportsGermany) {
        return parsed.filter(id =>
          TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_ID_SET.has(id),
        );
      }

      return parsed.filter(
        id => !TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET.has(id),
      );
    };

    if (fromFilter?.value) {
      const filtered = parseFlowIds(fromFilter.value);
      return filtered.length ? filtered : undefined;
    }

    if (isTipicoAffordabilitySportsGermany) {
      return TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_IDS;
    }

    if (isTipicoIdvSportsGermany) {
      return TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_IDS;
    }

    // Sonio Demo org: use sidebar selection, but hide the unwanted Tipico Sports flows.
    if (!sonioDemoFlowIds) return undefined;
    return sonioDemoFlowIds.filter(
      id => !TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET.has(id),
    );
  }, [
    savedFilters,
    sonioDemoFlowIds,
    isTipicoAffordabilitySportsGermany,
    isTipicoIdvSportsGermany,
  ]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDateDialogOpen, setExportDateDialogOpen] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({
    start: '',
    end: '',
  });
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const [filtersRowCollapsed, setFiltersRowCollapsed] = useState(false);
  const filtersRowRef = useRef<HTMLDivElement | null>(null);
  const [filtersRowMeasuredHeight, setFiltersRowMeasuredHeight] = useState(0);
  const [tableViewOpen, setTableViewOpen] = useState(false);
  const tableViewRef = useRef<HTMLDivElement | null>(null);
  const tableViewButtonRef = useRef<HTMLButtonElement | null>(null);
  const [_tableViewMode, _setTableViewMode] = useState<
    'fullName' | 'firstLast' | 'dateReviewed'
  >('fullName');
  const [_columnsOpen, _setColumnsOpen] = useState(false);
  const defaultColumns: Column[] = [
    'sonioId',
    'referenceId',
    'flowName',
    'fullName',
    'dateCreated',
    'status',
    'result',
    'assign',
  ];
  const [selectedColumns, setSelectedColumns] =
    useState<Column[]>(defaultColumns);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Debounce text filter updates so typing doesn't trigger fetch on every keypress.
  const textFilterDebounceTimersRef = useRef<
    Record<string, number | undefined>
  >({});

  const handleCopyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    const measure = () => {
      if (filtersRowRef.current) {
        // scrollHeight captures full content height regardless of current max-height
        setFiltersRowMeasuredHeight(filtersRowRef.current.scrollHeight);
      }
    };

    // Measure on mount and when dependencies change
    measure();

    // Re-measure on window resize for responsiveness
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [savedFilters, selectedColumns, openDropdown, filtersRowCollapsed]);

  // Dedupe identical fetches (React 18 StrictMode runs effects twice in dev)
  const lastFetchKeyRef = useRef<string>('');

  const getTransactions = useCallback(
    async (page: number, first: number, savedFilters: any[]) => {
      try {
        const { __raw } = (await getIdTokenClaims()) as any;
        dispatch(
          fetchTransactions({
            __raw,
            pageNumber: page,
            limit: first,
            authOrgId: selectedOrganization.id,
            filters: savedFilters,
            liveData: liveData,
            flowIds: effectiveFlowIdsForRequest,
          }),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch transactions:', err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      getIdTokenClaims,
      selectedOrganization.id,
      liveData,
      effectiveFlowIdsForRequest,
    ],
  );

  useEffect(() => {
    const fetchKey = JSON.stringify({
      page,
      first,
      orgId: selectedOrganization.id,
      savedFilters,
      liveData,
      flowIds: effectiveFlowIdsForRequest,
    });

    if (!filtersHydratedFromUrl) {
      return;
    }

    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }
    lastFetchKeyRef.current = fetchKey;

    getTransactions(page, first, savedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    first,
    selectedOrganization.id,
    savedFilters,
    liveData,
    effectiveFlowIdsForRequest,
    filtersHydratedFromUrl,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const filters: { key: string; label: string; value: string | string[] }[] =
      [];

    // Group values by key to handle multiple values for same key
    const groupedParams: Record<string, string[]> = {};
    queryParams.forEach((value, key) => {
      if (key === 'from') return;
      if (!groupedParams[key]) {
        groupedParams[key] = [];
      }
      groupedParams[key].push(value);
    });

    // Convert grouped params to filter objects
    Object.entries(groupedParams).forEach(([key, values]) => {
      const valueArray = values as string[];
      filters.push({
        key,
        label: key,
        value: valueArray.length === 1 ? valueArray[0] : valueArray,
      });
    });

    const sanitizedFilters = filters
      .map(f => {
        if (f.key !== 'flowId' && f.key !== 'flowIds' && f.key !== 'flowName') {
          return f;
        }

        const rawValues = Array.isArray(f.value)
          ? f.value
          : String(f.value).split(',');

        const normalizedValues = rawValues
          .flatMap(v => String(v).split(','))
          .map(v => v.trim())
          .filter(Boolean);

        const filteredValues = isTipicoAffordabilitySportsGermany
          ? normalizedValues.filter(v =>
              TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_ID_SET.has(v),
            )
          : isTipicoIdvSportsGermany
            ? normalizedValues.filter(v =>
                TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_ID_SET.has(v),
              )
            : normalizedValues.filter(
                v => !TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET.has(v),
              );

        if (filteredValues.length === 0) return null;

        return {
          ...f,
          value:
            filteredValues.length === 1 ? filteredValues[0] : filteredValues,
        };
      })
      .filter(
        (f): f is { key: string; label: string; value: string | string[] } =>
          Boolean(f),
      );

    setSavedFilters(sanitizedFilters);

    // Sync local filter state with saved filters
    const newLocalFilters = {
      sonioId: '',
      referenceId: '',
      flowName: [] as string[],
      fullName: '',
      firstName: '',
      lastName: '',
      dateRange: '',
      createdStartDate: '',
      createdEndDate: '',
      sortOrder: '',
      status: [] as string[],
      result: [] as string[],
      assignedUserId: '',
      assignedUserName: '',
      assigneeLabel: '',
    };

    sanitizedFilters.forEach(filter => {
      if (filter.key === 'id') {
        newLocalFilters.sonioId = filter.value as string;
      } else if (filter.key === 'referenceId') {
        newLocalFilters.referenceId = filter.value as string;
      } else if (filter.key === 'flowId' || filter.key === 'flowIds') {
        // Handle both flowId and flowIds keys
        // If value is a string with commas, split it into an array
        if (typeof filter.value === 'string' && filter.value.includes(',')) {
          newLocalFilters.flowName = filter.value.split(',');
        } else {
          newLocalFilters.flowName = Array.isArray(filter.value)
            ? filter.value
            : [filter.value as string];
        }
      } else if (filter.key === 'fullName') {
        newLocalFilters.fullName = filter.value as string;
      } else if (filter.key === 'firstName') {
        newLocalFilters.firstName = filter.value as string;
      } else if (filter.key === 'lastName') {
        newLocalFilters.lastName = filter.value as string;
      } else if (filter.key === 'createdStartDate') {
        newLocalFilters.createdStartDate = filter.value as string;
      } else if (filter.key === 'createdEndDate') {
        newLocalFilters.createdEndDate = filter.value as string;
      } else if (filter.key === 'sortOrder') {
        newLocalFilters.sortOrder = filter.value as string;
      } else if (filter.key === 'status') {
        newLocalFilters.status = Array.isArray(filter.value)
          ? filter.value
          : [filter.value as string];
      } else if (filter.key === 'validation') {
        newLocalFilters.result = Array.isArray(filter.value)
          ? filter.value
          : [filter.value as string];
      } else if (filter.key === 'assignedUserId') {
        newLocalFilters.assignedUserId = filter.value as string;
        newLocalFilters.assigneeLabel =
          filter.label !== 'assignedUserId' ? filter.label : 'Assignee';
      } else if (filter.key === 'assignedUserName') {
        newLocalFilters.assignedUserName = filter.value as string;
        newLocalFilters.assigneeLabel = String(filter.value);
      }
    });

    setFilters(newLocalFilters);

    // Sync temp date range with filters from URL
    setTempDateRange({
      start: newLocalFilters.createdStartDate || '',
      end: newLocalFilters.createdEndDate || '',
    });

    setFiltersHydratedFromUrl(true);
  }, [
    location.search,
    isTipicoAffordabilitySportsGermany,
    isTipicoIdvSportsGermany,
  ]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openDropdown &&
        !dropdownRefs.current[openDropdown]?.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (
    event: ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    setFirst(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearAllFilters = () => {
    setSavedFilters([]);
    localStorage.setItem('savedFilters', JSON.stringify([]));
    setFilters({
      sonioId: '',
      referenceId: '',
      flowName: [],
      fullName: '',
      firstName: '',
      lastName: '',
      dateRange: '',
      createdStartDate: '',
      createdEndDate: '',
      sortOrder: '',
      status: [],
      result: [],
      assignedUserId: '',
      assignedUserName: '',
      assigneeLabel: '',
    });
    setAssigneeSearch('');
    setTempDateRange({ start: '', end: '' });
    setPage(0);
    updateURLWithFilters([]);
    setFiltersOpen(false);
  };

  const handleRemoveFilter = (key: string) => {
    const updated = savedFilters.filter(f => f.key !== key);
    setSavedFilters(updated);
    localStorage.setItem('savedFilters', JSON.stringify(updated));
    setPage(0);
    updateURLWithFilters(updated);

    // Clear temp date range if removing date filters
    if (key === 'createdStartDate' || key === 'createdEndDate') {
      setTempDateRange({ start: '', end: '' });
    }
  };

  const handleRemoveFilterValue = (key: string, value: string) => {
    const target = savedFilters.find(f => f.key === key);
    if (!target) return;

    // Handle comma-separated string (like flowIds)
    if (typeof target.value === 'string' && target.value.includes(',')) {
      const values = target.value.split(',');
      const newValues = values.filter(v => v !== value);
      const updated = newValues.length
        ? savedFilters.map(f =>
            f.key === key ? { ...f, value: newValues.join(',') } : f,
          )
        : savedFilters.filter(f => f.key !== key);
      setSavedFilters(updated);
      localStorage.setItem('savedFilters', JSON.stringify(updated));
      setPage(0);
      updateURLWithFilters(updated);
    } else if (Array.isArray(target.value)) {
      const newValues = target.value.filter(v => v !== value);
      const updated = newValues.length
        ? savedFilters.map(f =>
            f.key === key ? { ...f, value: newValues } : f,
          )
        : savedFilters.filter(f => f.key !== key);
      setSavedFilters(updated);
      localStorage.setItem('savedFilters', JSON.stringify(updated));
      setPage(0);
      updateURLWithFilters(updated);
    } else {
      handleRemoveFilter(key);
    }
  };

  const _formatFilterValue = (value: string | string[]) =>
    Array.isArray(value) ? value.join(', ') : value;

  const handleViewClick = (rowId: string) => {
    const currentSearch = location.search;
    navigate(`/transactions/sessions/${rowId}${currentSearch}`);
  };

  const _colWidth = {
    sonioId: 200,
    referenceId: 160,
    fullName: 220,
    firstName: 140,
    lastName: 140,
    dateReviewed: 180,
    dateCreated: 180,
    status: 140,
    result: 120,
    assign: 60,
  } as const;

  const getGridTemplateColumns = () =>
    `repeat(${selectedColumns.length}, minmax(140px, 1fr))`;

  const tableMinWidth = (() => {
    const minColWidth = 140; // must match minmax above
    const gap = 10; // must match grid gap
    const paddingX = 16; // px-2 on row/header containers
    const cols = selectedColumns.length;
    const gapsTotal = Math.max(cols - 1, 0) * gap;
    return cols * minColWidth + gapsTotal + paddingX * 2;
  })();

  useEffect(() => {
    if (!tableViewOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        tableViewRef.current &&
        !tableViewRef.current.contains(e.target as Node)
      ) {
        setTableViewOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tableViewOpen]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('sessionsSelectedColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setSelectedColumns(parsed);
        }
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        'sessionsSelectedColumns',
        JSON.stringify(selectedColumns),
      );
    } catch (_) {}
  }, [selectedColumns]);

  const handleAssignClick = (session: any) => {
    setSelectedSession(session);
    setAssignModalOpen(true);
  };

  const toggleDropdown = (dropdownName: string) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const handleApplyDateRange = () => {
    if (!tempDateRange.start || !tempDateRange.end) return;

    // Remove any existing date filters first
    const withoutDateFilters = savedFilters.filter(
      f => f.key !== 'createdStartDate' && f.key !== 'createdEndDate',
    );

    // Add both date filters
    const newFilters = [
      ...withoutDateFilters,
      {
        key: 'createdStartDate',
        label: 'createdStartDate',
        value: tempDateRange.start,
      },
      {
        key: 'createdEndDate',
        label: 'createdEndDate',
        value: tempDateRange.end,
      },
    ];

    setSavedFilters(newFilters);
    localStorage.setItem('savedFilters', JSON.stringify(newFilters));
    setPage(0);
    updateURLWithFilters(newFilters);

    // Update the filter state for UI
    setFilters(prev => ({
      ...prev,
      createdStartDate: tempDateRange.start,
      createdEndDate: tempDateRange.end,
    }));
  };

  const handleFilterChange = (
    filterType: string,
    value: any,
    options?: { label?: string },
  ) => {
    // Update local filter state (for UI display)
    setFilters(prev => ({
      ...prev,
      [filterType]: value,
      ...(filterType === 'assignedUserId'
        ? {
            assignedUserName: '',
            assigneeLabel: options?.label || prev.assigneeLabel,
          }
        : {}),
      ...(filterType === 'assignedUserName'
        ? { assignedUserId: '', assigneeLabel: value || '' }
        : {}),
    }));

    // Map filterType to the correct filter key for API
    const filterKeyMap: Record<string, string> = {
      flowName: 'flowIds', // API expects flowIds (plural) with comma-separated values
      result: 'validation',
    };
    const filterKey = filterKeyMap[filterType] || filterType;
    const filterLabel =
      filterType === 'assignedUserId' || filterType === 'assignedUserName'
        ? options?.label || 'Assignee'
        : filterType;

    if (value && (Array.isArray(value) ? value.length > 0 : value.trim())) {
      // For flowName, convert array to comma-separated string
      const finalValue =
        filterType === 'flowName' && Array.isArray(value)
          ? value.join(',')
          : Array.isArray(value)
            ? value
            : value.toString();

      // assignedUserId and assignedUserName are mutually exclusive
      const withoutConflicting =
        filterType === 'assignedUserId' || filterType === 'assignedUserName'
          ? savedFilters.filter(
              f => f.key !== 'assignedUserId' && f.key !== 'assignedUserName',
            )
          : savedFilters.filter(f => f.key !== filterKey);

      const newFilter = {
        key: filterKey,
        label: filterLabel,
        value: finalValue,
      };
      const updatedFilters = [...withoutConflicting, newFilter];
      setSavedFilters(updatedFilters);
      localStorage.setItem('savedFilters', JSON.stringify(updatedFilters));
      setPage(0);
      updateURLWithFilters(updatedFilters);
    } else {
      const keysToClear =
        filterType === 'assignedUserId' || filterType === 'assignedUserName'
          ? ['assignedUserId', 'assignedUserName']
          : [filterKey];
      const updatedFilters = savedFilters.filter(
        f => !keysToClear.includes(f.key),
      );
      setSavedFilters(updatedFilters);
      localStorage.setItem('savedFilters', JSON.stringify(updatedFilters));
      updateURLWithFilters(updatedFilters);
    }
  };

  const loadOrgMembers = useCallback(async () => {
    if (!selectedOrganization?.id) {
      setOrgMembers([]);
      return;
    }
    setOrgMembersLoading(true);
    try {
      const { __raw } = (await getIdTokenClaims()) as any;
      const members = await getOrganizationMembers(
        selectedOrganization.id,
        __raw,
        { liveData },
      );
      setOrgMembers(members);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch organization members:', error);
      setOrgMembers([]);
    } finally {
      setOrgMembersLoading(false);
    }
  }, [getIdTokenClaims, liveData, selectedOrganization?.id]);

  useEffect(() => {
    if (openDropdown === 'assign') {
      setAssigneeSearch('');
      loadOrgMembers();
    }
  }, [openDropdown, loadOrgMembers]);

  const filteredOrgMembers = useMemo(() => {
    const q = assigneeSearch.trim().toLowerCase();
    if (!q) return orgMembers;
    return orgMembers.filter(member => {
      const label = getOrganizationMemberLabel(member).toLowerCase();
      const email = (member.email || '').toLowerCase();
      return label.includes(q) || email.includes(q);
    });
  }, [assigneeSearch, orgMembers]);

  // Resolve assignee display name after members load (URL only stores user_id)
  useEffect(() => {
    if (!filters.assignedUserId || orgMembers.length === 0) return;
    const member = orgMembers.find(m => m.user_id === filters.assignedUserId);
    if (!member) return;
    const label = getOrganizationMemberLabel(member);
    if (filters.assigneeLabel === label) return;
    setFilters(prev => ({ ...prev, assigneeLabel: label }));
    setSavedFilters(prev =>
      prev.map(f => (f.key === 'assignedUserId' ? { ...f, label } : f)),
    );
  }, [orgMembers, filters.assignedUserId, filters.assigneeLabel]);

  const handleDebouncedTextFilterChange = (
    filterType: string,
    value: string,
  ) => {
    // Always keep local UI state in sync immediately
    setFilters(prev => ({
      ...prev,
      [filterType]: value,
    }));

    const existing = textFilterDebounceTimersRef.current[filterType];
    if (existing) {
      window.clearTimeout(existing);
    }

    textFilterDebounceTimersRef.current[filterType] = window.setTimeout(() => {
      handleFilterChange(filterType, value);
    }, 1000);
  };
  const updateURLWithFilters = (
    filters: Array<{ key: string; label: string; value: string | string[] }>,
  ) => {
    const queryParams = new URLSearchParams();
    filters.forEach(filter => {
      if (Array.isArray(filter.value)) {
        filter.value.forEach(value => {
          queryParams.append(filter.key, value);
        });
      } else if (typeof filter.value === 'string') {
        queryParams.append(filter.key, filter.value);
      }
    });

    navigate({
      pathname: location.pathname,
      search: `?${queryParams.toString()}`,
    });
  };

  const updateFilters = (newFilter: {
    key: string;
    label: string;
    value: string | string[];
  }) => {
    const updatedFilters = savedFilters.filter(f => f.key !== newFilter.key);
    updatedFilters.push(newFilter);

    setSavedFilters(updatedFilters);
    localStorage.setItem('savedFilters', JSON.stringify(updatedFilters));
    setPage(0);

    updateURLWithFilters(updatedFilters);
  };

  const handleAssign = async (user: any) => {
    try {
      if (!selectedSession?.sessionId) {
        // eslint-disable-next-line no-console
        console.error('No session selected');
        return;
      }
      const { __raw } = (await getIdTokenClaims()) as any;
      await axios.post(
        `${getCmsApiBaseUrl(liveData)}/user-session-assign`,
        {
          user_id: user.user_id,
          session_id: selectedSession.sessionId,
        },
        {
          headers: {
            Authorization: `Bearer ${__raw}`,
          },
        },
      );
      getTransactions(page, first, savedFilters);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to assign session:', error);
    }
  };

  const handleRemove = async (user: any) => {
    try {
      if (!selectedSession?.sessionId) {
        // eslint-disable-next-line no-console
        console.error('No session selected');
        return;
      }
      const { __raw } = (await getIdTokenClaims()) as any;
      await axios.delete(`${getCmsApiBaseUrl(liveData)}/user-session-assign`, {
        data: {
          user_id: user.user_id,
          session_id: selectedSession.sessionId,
        },
        headers: {
          Authorization: `Bearer ${__raw}`,
        },
      });
      getTransactions(page, first, savedFilters);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to remove user from session:', error);
    }
  };

  const hasDateRange = (filters: any) => {
    const dateStartFilter = filters.find(f => f.key === 'createdStartDate');
    const dateEndFilter = filters.find(f => f.key === 'createdEndDate');
    return !!(dateStartFilter?.value && dateEndFilter?.value);
  };

  const handleExport = (filters: any) => {
    if (!hasDateRange(filters)) {
      // Open date range dialog
      setExportDateDialogOpen(true);
      // Pre-fill with existing dates if any
      const dateStartFilter = filters.find(f => f.key === 'createdStartDate');
      const dateEndFilter = filters.find(f => f.key === 'createdEndDate');
      setExportDateRange({
        start: dateStartFilter?.value || '',
        end: dateEndFilter?.value || '',
      });
      return;
    }
    // If dates exist, proceed with export
    performExport(filters);
  };

  const handleConfirmExportDateRange = () => {
    if (!exportDateRange.start || !exportDateRange.end) {
      return;
    }
    setExportDateDialogOpen(false);
    // Proceed with export using the selected dates
    performExport(savedFilters, exportDateRange.start, exportDateRange.end);
    // Reset date range
    setExportDateRange({ start: '', end: '' });
  };

  const performExport = async (
    filters: any,
    dateStart?: string,
    dateEnd?: string,
  ) => {
    setIsExporting(true);

    (async () => {
      try {
        const { __raw } = (await getIdTokenClaims()) as any;
        const queryParams = new URLSearchParams();

        // Use provided dates or get from filters
        const dateStartValue =
          dateStart || filters.find(f => f.key === 'createdStartDate')?.value;
        const dateEndValue =
          dateEnd || filters.find(f => f.key === 'createdEndDate')?.value;

        if (dateStartValue) {
          queryParams.append('createdStartDate', String(dateStartValue));
        }
        if (dateEndValue) {
          queryParams.append('createdEndDate', String(dateEndValue));
        }

        if (selectedOrganization?.customerId) {
          queryParams.append('customerId', selectedOrganization.customerId);
        }

        filters.forEach(filter => {
          if (
            filter.key === 'createdStartDate' ||
            filter.key === 'createdEndDate'
          ) {
            return;
          }

          const apiKeyMap: Record<string, string> = {
            flowIds: 'flowIds',
            validation: 'validation',
            status: 'status',
            state: 'state',
            poiValidation: 'poiValidation',
            poaValidation: 'poaValidation',
            id: 'id',
            referenceId: 'referenceId',
          };

          const apiKey = apiKeyMap[filter.key] || filter.key;

          if (filter.value) {
            if (Array.isArray(filter.value)) {
              filter.value.forEach(value => {
                queryParams.append(apiKey, value);
              });
            } else {
              queryParams.append(apiKey, filter.value.toString());
            }
          }
        });

        const response = await axios.get(
          `${getCmsApiBaseUrl(liveData)}/sessions/export?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${__raw}`,
            },
            responseType: 'blob',
          },
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers['content-disposition'];
        let filename = `sessions-export-${new Date().toISOString()}.csv`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // Export completed successfully
        setIsExporting(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to export sessions:', error);
        setIsExporting(false);
      }
    })();
  };

  return (
    <div className="space-y-5 px-4 sm:px-8 py-7 bg-[#F7F9FC] min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {fromStatistics && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/transactions/statistics')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Statistics
            </Button>
          )}
          <div>
            <h1 className="text-[26px] font-extrabold tracking-[-0.025em] text-[#0C2340]">
              Sessions
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-1">
              Every verification session across your flows. Click a column
              header to filter it.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport(savedFilters)}
            disabled={isExporting}
          >
            <FileDown className="h-4 w-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          <Button
            ref={filterButtonRef}
            variant="outline"
            size="sm"
            onClick={() => setFiltersOpen(true)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button
            ref={tableViewButtonRef}
            variant="ghost"
            size="sm"
            onClick={() => {
              setTableViewOpen(prev => !prev);
            }}
          >
            <Table className="h-4 w-4 mr-2" />
            Table View
          </Button>
        </div>
      </div>

      {/* Active Filters Row */}
      <ActiveFiltersRow
        savedFilters={savedFilters}
        filtersRowCollapsed={filtersRowCollapsed}
        filtersRowMeasuredHeight={filtersRowMeasuredHeight}
        filtersRowRef={filtersRowRef}
        onToggleCollapse={() => setFiltersRowCollapsed(!filtersRowCollapsed)}
        onClearAll={clearAllFilters}
        onRemoveFilter={handleRemoveFilter}
        onRemoveFilterValue={handleRemoveFilterValue}
      />

      {/* Table Card */}
      <div className="overflow-x-auto bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px]">
        <div className="p-0 w-full" style={{ minWidth: `${tableMinWidth}px` }}>
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00387D]"></div>
                <p className="text-[#5B6B80]">Fetching data…</p>
              </div>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div
                className="bg-[#FAFBFD] border-b border-[#E5E7EB] rounded-t-[14px]"
                style={{ height: '48px' }}
              >
                <div
                  className="grid items-center h-full px-2 text-center"
                  style={{
                    gap: 10,
                    gridTemplateColumns: getGridTemplateColumns(),
                  }}
                >
                  {/* SONIO ID */}
                  {selectedColumns.includes('sonioId') && (
                    <div className="relative">
                      <div
                        className={`text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md group ${
                          openDropdown === 'sonioId' || filters.sonioId
                            ? 'bg-[#EAF0F9] text-[#00387D]'
                            : 'text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#0C2340]'
                        }`}
                        onClick={() => toggleDropdown('sonioId')}
                      >
                        <span
                          className="truncate"
                          style={{ whiteSpace: 'nowrap' }}
                          title="SONIO ID"
                        >
                          SONIO ID
                        </span>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                      {openDropdown === 'sonioId' && (
                        <div
                          ref={el => (dropdownRefs.current['sonioId'] = el)}
                          className="absolute top-full left-0 mt-1 w-64 bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] z-10 text-left normal-case tracking-normal font-normal p-3"
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="Search"
                              value={filters.sonioId}
                              onChange={e =>
                                handleDebouncedTextFilterChange(
                                  'id',
                                  e.target.value,
                                )
                              }
                              className="flex-1 px-2 py-1 border-[1.5px] border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#00387D] focus:ring-2 focus:ring-[#00387D]/20"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reference ID */}
                  {selectedColumns.includes('referenceId') && (
                    <div className="relative">
                      <div
                        className={`text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md group ${
                          openDropdown === 'referenceId' || filters.referenceId
                            ? 'bg-[#EAF0F9] text-[#00387D]'
                            : 'text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#0C2340]'
                        }`}
                        onClick={() => toggleDropdown('referenceId')}
                      >
                        <span
                          className="truncate"
                          style={{ whiteSpace: 'nowrap' }}
                          title="Reference ID"
                        >
                          Reference ID
                        </span>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                      {openDropdown === 'referenceId' && (
                        <div
                          ref={el => (dropdownRefs.current['referenceId'] = el)}
                          className="absolute top-full left-0 mt-1 w-64 bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] z-10 text-left normal-case tracking-normal font-normal p-3"
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="Search reference id"
                              value={filters.referenceId}
                              onChange={e =>
                                handleDebouncedTextFilterChange(
                                  'referenceId',
                                  e.target.value,
                                )
                              }
                              className="flex-1 px-2 py-1 border-[1.5px] border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#00387D] focus:ring-2 focus:ring-[#00387D]/20"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Flow Name */}
                  {selectedColumns.includes('flowName') && (
                    <div className="relative">
                      <div
                        className={`text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md group ${
                          openDropdown === 'flowName' ||
                          filters.flowName.length > 0
                            ? 'bg-[#EAF0F9] text-[#00387D]'
                            : 'text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#0C2340]'
                        }`}
                        onClick={() => toggleDropdown('flowName')}
                      >
                        <span
                          className="truncate"
                          style={{ whiteSpace: 'nowrap' }}
                          title="Flow Name"
                        >
                          Flow Name
                        </span>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                      {openDropdown === 'flowName' &&
                        (() => {
                          // When Sonio Demo is selected, show only the selected flows (sidebar/profile); otherwise org flowIds or all orgs
                          const allFlowIds =
                            isSonioDemoOrg &&
                            sonioDemoFlowIds &&
                            sonioDemoFlowIds.length > 0
                              ? sonioDemoFlowIds
                              : selectedOrganization?.id === '' ||
                                  !selectedOrganization
                                ? Array.from(
                                    new Set(
                                      organizations
                                        .filter(
                                          org =>
                                            org.flowIds &&
                                            org.flowIds.length > 0,
                                        )
                                        .flatMap(org => org.flowIds || []),
                                    ),
                                  )
                                : selectedOrganization?.flowIds || [];

                          const displayedFlowIds =
                            isTipicoAffordabilitySportsGermany
                              ? allFlowIds.filter(flowId =>
                                  TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_ID_SET.has(
                                    flowId,
                                  ),
                                )
                              : isTipicoIdvSportsGermany
                                ? allFlowIds.filter(flowId =>
                                    TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_ID_SET.has(
                                      flowId,
                                    ),
                                  )
                                : allFlowIds.filter(
                                    flowId =>
                                      !TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET.has(
                                        flowId,
                                      ),
                                  );

                          return (
                            <div
                              ref={el =>
                                (dropdownRefs.current['flowName'] = el)
                              }
                              className="absolute top-full left-0 mt-1 w-64 bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] z-10 text-left normal-case tracking-normal font-normal p-3 max-h-64 overflow-y-auto"
                            >
                              {displayedFlowIds.length > 0 ? (
                                <div className="space-y-2 mb-3">
                                  {displayedFlowIds.map(flowId => {
                                    const flowName =
                                      flowNameLookup[flowId] || flowId;
                                    return (
                                      <div
                                        key={flowId}
                                        className="flex items-center gap-2"
                                      >
                                        <input
                                          type="checkbox"
                                          id={`flowId-${flowId}`}
                                          checked={filters.flowName.includes(
                                            flowId,
                                          )}
                                          onChange={e => {
                                            if (e.target.checked) {
                                              handleFilterChange('flowName', [
                                                ...filters.flowName,
                                                flowId,
                                              ]);
                                            } else {
                                              handleFilterChange(
                                                'flowName',
                                                filters.flowName.filter(
                                                  f => f !== flowId,
                                                ),
                                              );
                                            }
                                          }}
                                          className="w-4 h-4 accent-[#00387D]"
                                          style={{
                                            accentColor: '#00387D',
                                          }}
                                        />
                                        <label
                                          htmlFor={`flowId-${flowId}`}
                                          className="text-sm cursor-pointer"
                                          title={flowId}
                                        >
                                          {flowName}
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-sm text-[#5B6B80] py-2">
                                  No flows available
                                </div>
                              )}
                            </div>
                          );
                        })()}
                    </div>
                  )}

                  {/* Full Name */}
                  {selectedColumns.includes('fullName') && (
                    <div className="relative">
                      <div
                        className={`text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md group ${
                          openDropdown === 'fullName' || filters.fullName
                            ? 'bg-[#EAF0F9] text-[#00387D]'
                            : 'text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#0C2340]'
                        }`}
                        onClick={() => toggleDropdown('fullName')}
                      >
                        <span
                          className="truncate"
                          style={{ whiteSpace: 'nowrap' }}
                          title="Full Name"
                        >
                          Full Name
                        </span>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                      {openDropdown === 'fullName' && (
                        <div
                          ref={el => (dropdownRefs.current['fullName'] = el)}
                          className="absolute top-full left-0 mt-1 w-64 bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] z-10 text-left normal-case tracking-normal font-normal p-3"
                        >
                          <div className="space-y-2 mb-3">
                            <div className="flex items-center gap-2">
                              <Search className="w-4 h-4 text-[#5B6B80]" />
                              <input
                                type="text"
                                placeholder="Search name..."
                                value={filters.fullName}
                                onChange={e =>
                                  handleDebouncedTextFilterChange(
                                    'fullName',
                                    e.target.value,
                                  )
                                }
                                className="flex-1 px-2 py-1 border-[1.5px] border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#00387D] focus:ring-2 focus:ring-[#00387D]/20"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* First Name */}
                  {selectedColumns.includes('firstName') && (
                    <div className="relative">
                      <div
                        className={`text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md group ${
                          openDropdown === 'firstName' || filters.firstName
                            ? 'bg-[#EAF0F9] text-[#00387D]'
                            : 'text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#0C2340]'
                        }`}
                        onClick={() => toggleDropdown('firstName')}
                      >
                        <span
                          className="truncate"
                          style={{ whiteSpace: 'nowrap' }}
                          title="First Name"
                        >
                          First Name
                        </span>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                      {openDropdown === 'firstName' && (
                        <div
                          ref={el => (dropdownRefs.current['firstName'] = el)}
                          className="absolute top-full left-0 mt-1 w-64 bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] z-10 text-left normal-case tracking-normal font-normal p-3"
                        >
                          <div className="space-y-2 mb-3">
                            <div className="flex items-center gap-2">
                              <Search className="w-4 h-4 text-[#5B6B80]" />
                              <input
                                type="text"
                                placeholder="Search first name..."
                                value={filters.firstName}
                                onChange={e =>
                                  handleDebouncedTextFilterChange(
                                    'firstName',
                                    e.target.value,
                                  )
                                }
                                className="flex-1 px-2 py-1 border-[1.5px] border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#00387D] focus:ring-2 focus:ring-[#00387D]/20"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Last Name */}
                  {selectedColumns.includes('lastName') && (
                    <div className="relative">
                      <div
                        className={`text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md group ${
                          openDropdown === 'lastName' || filters.lastName
                            ? 'bg-[#EAF0F9] text-[#00387D]'
                            : 'text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#0C2340]'
                        }`}
                        onClick={() => toggleDropdown('lastName')}
                      >
                        <span
                          className="truncate"
                          style={{ whiteSpace: 'nowrap' }}
                          title="Last Name"
                        >
                          Last Name
                        </span>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                      {openDropdown === 'lastName' && (
                        <div
                          ref={el => (dropdownRefs.current['lastName'] = el)}
                          className="absolute top-full left-0 mt-1 w-64 bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] z-10 text-left normal-case tracking-normal font-normal p-3"
                        >
                          <div className="space-y-2 mb-3">
                            <div className="flex items-center gap-2">
                              <Search className="w-4 h-4 text-[#5B6B80]" />
                              <input
                                type="text"
                                placeholder="Search last name..."
                                value={filters.lastName}
                                onChange={e =>
                                  handleDebouncedTextFilterChange(
                                    'lastName',
                                    e.target.value,
                                  )
                                }
                                className="flex-1 px-2 py-1 border-[1.5px] border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#00387D] focus:ring-2 focus:ring-[#00387D]/20"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Date Reviewed */}
                  {selectedColumns.includes('dateReviewed') && (
                    <div className="relative">
                      <div className="font-semibold text-[#0C2340] px-3 py-1 rounded-lg text-center">
                        <span
                          className="truncate"
                          style={{ whiteSpace: 'nowrap' }}
                          title="Date Reviewed"
                        >
                          Date Reviewed
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Date Created */}
                  {selectedColumns.includes('dateCreated') && (
                    <div className="relative">
                      <div
                        className={`text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md group ${
                          openDropdown === 'dateCreated' ||
                          filters.createdStartDate ||
                          filters.createdEndDate ||
                          filters.sortOrder
                            ? 'bg-[#EAF0F9] text-[#00387D]'
                            : 'text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#0C2340]'
                        }`}
                        onClick={() => toggleDropdown('dateCreated')}
                      >
                        <span className="truncate">Timeframe</span>
                        <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                      </div>
                      {openDropdown === 'dateCreated' && (
                        <div
                          ref={el => (dropdownRefs.current['dateCreated'] = el)}
                          className="absolute top-full left-0 mt-1 w-72 bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] z-10 text-left normal-case tracking-normal font-normal overflow-hidden"
                        >
                          {/* Sort Order Section */}
                          <div className="p-4 bg-[#F7F9FC] border-b border-[#E5E7EB]">
                            <div className="text-xs font-semibold text-[#252525] mb-3 uppercase tracking-wide">
                              Sort Order
                            </div>
                            <div className="space-y-2.5">
                              <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-white transition-colors group">
                                <input
                                  type="radio"
                                  name="sortOrder"
                                  value="newest"
                                  checked={filters.sortOrder === 'newest'}
                                  onChange={e =>
                                    handleFilterChange(
                                      'sortOrder',
                                      e.target.value,
                                    )
                                  }
                                  className="w-4 h-4 accent-[#00387D]"
                                />
                                <span className="text-sm text-[#252525] group-hover:text-[#00387D] transition-colors">
                                  Newest to Oldest
                                </span>
                              </label>
                              <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-white transition-colors group">
                                <input
                                  type="radio"
                                  name="sortOrder"
                                  value="oldest"
                                  checked={filters.sortOrder === 'oldest'}
                                  onChange={e =>
                                    handleFilterChange(
                                      'sortOrder',
                                      e.target.value,
                                    )
                                  }
                                  className="w-4 h-4 accent-[#00387D]"
                                />
                                <span className="text-sm text-[#252525] group-hover:text-[#00387D] transition-colors">
                                  Oldest to Newest
                                </span>
                              </label>
                            </div>
                          </div>

                          {/* Date Range Section */}
                          <div className="p-4">
                            <div className="text-xs font-semibold text-[#252525] mb-2 uppercase tracking-wide">
                              Date Range
                            </div>
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-[#6B6B6B] font-medium w-12">
                                  From:
                                </label>
                                <div className="flex-1 relative">
                                  <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5B6B80] pointer-events-none" />
                                  <input
                                    type="date"
                                    value={tempDateRange.start}
                                    onChange={e =>
                                      setTempDateRange(prev => ({
                                        ...prev,
                                        start: e.target.value,
                                      }))
                                    }
                                    className="w-full pl-8 pr-2 py-2 border-[1.5px] border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#00387D] focus:ring-2 focus:ring-[#00387D]/20"
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-[#6B6B6B] font-medium w-12">
                                  To:
                                </label>
                                <div className="flex-1 relative">
                                  <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5B6B80] pointer-events-none" />
                                  <input
                                    type="date"
                                    value={tempDateRange.end}
                                    onChange={e =>
                                      setTempDateRange(prev => ({
                                        ...prev,
                                        end: e.target.value,
                                      }))
                                    }
                                    className="w-full pl-8 pr-2 py-2 border-[1.5px] border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#00387D] focus:ring-2 focus:ring-[#00387D]/20"
                                  />
                                </div>
                              </div>

                              {/* Apply Date Range Button */}
                              {tempDateRange.start && tempDateRange.end && (
                                <button
                                  onClick={handleApplyDateRange}
                                  className="w-full mt-2 px-3 py-2 bg-[#00387D] text-white rounded-lg text-sm font-medium hover:bg-[#00387D]/90 transition-colors"
                                >
                                  Apply Date Range
                                </button>
                              )}

                              {(filters.createdStartDate ||
                                filters.createdEndDate ||
                                tempDateRange.start ||
                                tempDateRange.end) && (
                                <button
                                  onClick={() => {
                                    setTempDateRange({ start: '', end: '' });
                                    // Remove date filters from saved filters
                                    const withoutDateFilters =
                                      savedFilters.filter(
                                        f =>
                                          f.key !== 'createdStartDate' &&
                                          f.key !== 'createdEndDate',
                                      );
                                    setSavedFilters(withoutDateFilters);
                                    localStorage.setItem(
                                      'savedFilters',
                                      JSON.stringify(withoutDateFilters),
                                    );
                                    setPage(0);
                                    updateURLWithFilters(withoutDateFilters);
                                    // Clear filter state
                                    setFilters(prev => ({
                                      ...prev,
                                      createdStartDate: '',
                                      createdEndDate: '',
                                    }));
                                  }}
                                  className="w-full mt-2 text-[#00387D] hover:text-[#00387D]/80 text-xs font-medium underline"
                                >
                                  Clear Date Range
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Clear All Button */}
                          {(filters.sortOrder ||
                            filters.createdStartDate ||
                            filters.createdEndDate) && (
                            <div className="p-3 bg-[#F7F9FC] border-t border-[#E5E7EB]">
                              <button
                                onClick={() => {
                                  setTempDateRange({ start: '', end: '' });
                                  // Remove all timeframe filters
                                  const withoutTimeframeFilters =
                                    savedFilters.filter(
                                      f =>
                                        f.key !== 'createdStartDate' &&
                                        f.key !== 'createdEndDate' &&
                                        f.key !== 'sortOrder',
                                    );
                                  setSavedFilters(withoutTimeframeFilters);
                                  localStorage.setItem(
                                    'savedFilters',
                                    JSON.stringify(withoutTimeframeFilters),
                                  );
                                  setPage(0);
                                  updateURLWithFilters(withoutTimeframeFilters);
                                  // Clear filter state
                                  setFilters(prev => ({
                                    ...prev,
                                    sortOrder: '',
                                    createdStartDate: '',
                                    createdEndDate: '',
                                  }));
                                }}
                                className="w-full px-3 py-2 text-sm text-[#00387D] hover:bg-white rounded-lg transition-colors font-medium"
                              >
                                Clear All Timeframe Filters
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Status */}
                  {selectedColumns.includes('status') && (
                    <div className="relative">
                      <div
                        className={`text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md group ${
                          openDropdown === 'status' || filters.status.length > 0
                            ? 'bg-[#EAF0F9] text-[#00387D]'
                            : 'text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#0C2340]'
                        }`}
                        onClick={() => toggleDropdown('status')}
                      >
                        <span>Status</span>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                      {openDropdown === 'status' && (
                        <div
                          ref={el => (dropdownRefs.current['status'] = el)}
                          className="absolute top-full left-0 mt-1 w-64 bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] z-10 text-left normal-case tracking-normal font-normal p-3"
                        >
                          <div className="space-y-2 mb-3">
                            {[
                              'STARTED',
                              'COMPLETED',
                              'PENDING',
                              'CANCELED',
                            ].map(status => (
                              <div
                                key={status}
                                className="flex items-center gap-2"
                              >
                                <input
                                  type="checkbox"
                                  id={`status-${status}`}
                                  checked={filters.status.includes(status)}
                                  onChange={e => {
                                    if (e.target.checked) {
                                      handleFilterChange('status', [
                                        ...filters.status,
                                        status,
                                      ]);
                                    } else {
                                      handleFilterChange(
                                        'status',
                                        filters.status.filter(
                                          s => s !== status,
                                        ),
                                      );
                                    }
                                  }}
                                  className="w-4 h-4 accent-[#00387D]"
                                />
                                <label
                                  htmlFor={`status-${status}`}
                                  className="text-sm"
                                >
                                  {status}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Result */}
                  {selectedColumns.includes('result') && (
                    <div className="relative">
                      <div
                        className={`text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md group ${
                          openDropdown === 'result' || filters.result.length > 0
                            ? 'bg-[#EAF0F9] text-[#00387D]'
                            : 'text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#0C2340]'
                        }`}
                        onClick={() => toggleDropdown('result')}
                      >
                        <span>Result</span>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                      {openDropdown === 'result' && (
                        <div
                          ref={el => (dropdownRefs.current['result'] = el)}
                          className="absolute top-full left-0 mt-1 w-64 bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] z-10 text-left normal-case tracking-normal font-normal p-3"
                        >
                          <div className="space-y-2 mb-3">
                            {['PASS', 'FAIL', 'PENDING', 'NEEDS_REVIEW'].map(
                              result => (
                                <div
                                  key={result}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    type="checkbox"
                                    id={`result-${result}`}
                                    checked={filters.result.includes(result)}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        handleFilterChange('validation', [
                                          ...filters.result,
                                          result,
                                        ]);
                                      } else {
                                        handleFilterChange(
                                          'validation',
                                          filters.result.filter(
                                            r => r !== result,
                                          ),
                                        );
                                      }
                                    }}
                                    className="w-4 h-4 accent-[#00387D]"
                                  />
                                  <label
                                    htmlFor={`result-${result}`}
                                    className="text-sm"
                                  >
                                    {result.replace(/_/g, ' ')}
                                  </label>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Assign / Assignee */}
                  {selectedColumns.includes('assign') && (
                    <div className="relative">
                      <div
                        className={`text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md group ${
                          openDropdown === 'assign' ||
                          filters.assignedUserId ||
                          filters.assignedUserName
                            ? 'bg-[#EAF0F9] text-[#00387D]'
                            : 'text-[#5B6B80] hover:bg-[#F7F9FC] hover:text-[#0C2340]'
                        }`}
                        onClick={() => toggleDropdown('assign')}
                      >
                        <span
                          className="truncate"
                          style={{ whiteSpace: 'nowrap' }}
                          title="Assignee"
                        >
                          Assign
                        </span>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                      {openDropdown === 'assign' && (
                        <div
                          ref={el => (dropdownRefs.current['assign'] = el)}
                          className="absolute top-full right-0 mt-1 w-72 bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] z-10 text-left normal-case tracking-normal font-normal p-3"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="text"
                              placeholder="Search members"
                              value={assigneeSearch}
                              onChange={e => setAssigneeSearch(e.target.value)}
                              onKeyDown={e => {
                                if (e.key !== 'Enter') return;
                                e.preventDefault();
                                const name = assigneeSearch.trim();
                                if (!name) return;
                                handleFilterChange('assignedUserName', name);
                                setOpenDropdown(null);
                              }}
                              className="flex-1 px-2 py-1 border-[1.5px] border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#00387D] focus:ring-2 focus:ring-[#00387D]/20"
                            />
                          </div>
                          <div className="max-h-56 overflow-y-auto space-y-1">
                            {orgMembersLoading ? (
                              <p className="text-xs text-[#5B6B80] px-1 py-2">
                                Loading members…
                              </p>
                            ) : filteredOrgMembers.length === 0 ? (
                              <div className="space-y-2">
                                <p className="text-xs text-[#5B6B80] px-1 py-1">
                                  No members found
                                </p>
                                {assigneeSearch.trim() && (
                                  <button
                                    type="button"
                                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-[#F7F9FC] text-[#00387D]"
                                    onClick={() => {
                                      handleFilterChange(
                                        'assignedUserName',
                                        assigneeSearch.trim(),
                                      );
                                      setOpenDropdown(null);
                                    }}
                                  >
                                    Filter by name “{assigneeSearch.trim()}”
                                  </button>
                                )}
                              </div>
                            ) : (
                              filteredOrgMembers.map(member => {
                                const label =
                                  getOrganizationMemberLabel(member);
                                const selected =
                                  filters.assignedUserId === member.user_id;
                                return (
                                  <button
                                    key={member.user_id}
                                    type="button"
                                    className={`w-full text-left text-sm px-2 py-1.5 rounded ${
                                      selected
                                        ? 'bg-[#00387D]/10 text-[#00387D]'
                                        : 'hover:bg-[#F7F9FC] text-[#252525]'
                                    }`}
                                    onClick={() => {
                                      handleFilterChange(
                                        'assignedUserId',
                                        selected ? '' : member.user_id,
                                        { label },
                                      );
                                      setOpenDropdown(null);
                                    }}
                                  >
                                    <div className="font-medium truncate">
                                      {label}
                                    </div>
                                    {member.email && (
                                      <div className="text-xs text-[#5B6B80] truncate">
                                        {member.email}
                                      </div>
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                          {(filters.assignedUserId ||
                            filters.assignedUserName) && (
                            <button
                              type="button"
                              className="mt-2 w-full text-xs text-[#00387D] hover:underline"
                              onClick={() => {
                                handleFilterChange('assignedUserId', '');
                                setAssigneeSearch('');
                                setOpenDropdown(null);
                              }}
                            >
                              Clear assignee filter
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Table Rows */}
              {totalCount === 0 ? (
                <div className="flex items-center justify-center h-96">
                  <p className="text-[#5B6B80]">No session found</p>
                </div>
              ) : (
                <div>
                  {sliceTransactions?.map(session => (
                    <SessionsTableRow
                      key={session.sessionId}
                      session={session}
                      selectedColumns={selectedColumns}
                      gridTemplateColumns={getGridTemplateColumns()}
                      copiedId={copiedId}
                      onCopyToClipboard={handleCopyToClipboard}
                      onViewClick={handleViewClick}
                      onAssignClick={handleAssignClick}
                      flowNameLookup={flowNameLookup}
                    />
                  ))}
                </div>
              )}

              {/* Pagination */}
              <div className="flex items-end justify-end px-4 py-1 border-t border-[#E5E7EB]">
                <TablePagination
                  component="div"
                  count={totalCount}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleLimitChange as any}
                  page={page}
                  rowsPerPage={first}
                  rowsPerPageOptions={[10, 20, 40]}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <AssignModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onAssign={handleAssign}
        onRemove={handleRemove}
        _session={selectedSession}
      />

      {/* Export Date Range Dialog */}
      <ExportDateDialog
        open={exportDateDialogOpen}
        onOpenChange={setExportDateDialogOpen}
        exportDateRange={exportDateRange}
        onDateChange={setExportDateRange}
        onConfirm={handleConfirmExportDateRange}
      />

      <NewResultsFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        anchorEl={filterButtonRef.current}
        onAddFilter={(key, value) => {
          const newFilter = { key, label: key, value } as any;
          updateFilters(newFilter);
        }}
        defaults={{
          id: filters.sonioId,
          referenceId: filters.referenceId,
          flowId: Array.isArray(filters.flowName)
            ? filters.flowName.join(',')
            : filters.flowName,
          fullName: filters.fullName,
          firstName: filters.firstName,
          lastName: filters.lastName,
          createdStartDate: filters.createdStartDate,
          createdEndDate: filters.createdEndDate,
          sortOrder: filters.sortOrder,
        }}
      />
      <TableViewModal
        ref={tableViewRef}
        open={tableViewOpen}
        onClose={() => setTableViewOpen(false)}
        anchorEl={tableViewButtonRef.current}
        selectedColumns={selectedColumns}
        onColumnChange={setSelectedColumns}
      />
    </div>
  );
};

export default Results;
